/**
 * Todo o texto da campanha em um lugar só.
 *
 * Fica separado do JSX porque quem revisa o texto (produto e marketing) não é
 * quem mexe no componente. O texto segue a comunicação oficial da campanha
 * (www.cakto.com.br/black-friday, seção do troféu): o nome é **Black Cakto
 * 100K** e a peça é o **troféu**. O código e o slug da API continuam
 * `black-november`, que é o nome interno.
 *
 * `{year}`, `{goal}`, `{goalShort}` e `{sales}` são preenchidos com o que a API
 * responde (`fillCampaignText`). Nenhum ano fica escrito aqui: a campanha volta
 * em 2027 sem ninguém revisar este arquivo.
 *
 * Apuração e entrega ficam com o regulamento, como no site. Este texto não
 * promete nada sobre a peça que a comunicação oficial não promete (gravação,
 * tiragem): o que ele afirma é só o que o backend mede, venda aprovada dentro
 * da janela.
 */

export const BLACK_NOVEMBER_COPY = {
  campaign: 'Black Cakto 100K',
  campaignWithYear: 'Black Cakto 100K {year}',
  award: 'Troféu Black Cakto 100K',
  period: '1º a 30 de novembro',
  periodChip: {
    days: '01 — 30',
    month: 'Novembro',
    label: 'Período da meta',
  },

  strip: {
    teaser: {
      tag: 'Em breve',
      headline: 'Começa em 1º de novembro',
      aside: '{goalShort} em vendas valem o troféu',
      action: 'Como conquistar',
    },
    running: {
      tag: 'Até 30/11',
      action: 'Ver detalhes',
    },
    achieved: {
      tag: 'Meta batida',
      aside: 'O troféu é seu · e o mês não acabou',
      action: 'Ver meu troféu',
    },
    won: {
      tag: 'Troféu conquistado',
      aside: 'a gente vai combinar a entrega',
      action: 'Ver meu troféu',
    },
    missed: {
      tag: 'Encerrado',
      aside: 'veja como foi seu novembro',
      action: 'Ver meu novembro',
    },
    missing: 'Faltam',
    progress: 'Progresso até o troféu',
    dismiss: 'Dispensar o aviso da campanha',
  },

  dialog: {
    close: 'Fechar',
    title: 'Black Cakto 100K.',
    titleMuted: 'O troféu desta Black.',

    running: {
      eyebrow: 'Em andamento · até 30 de novembro',
      teaserEyebrow: 'Começa em 1º de novembro',
      lead: 'Um reconhecimento para quem alcançar {goalShort} em vendas pela Cakto de 1 a 30 de novembro, conforme o regulamento.',
      stepsTitle: 'Como conquistar',
      stepsTitleMuted: 'o troféu.',
      steps: [
        {
          title: 'Venda durante novembro',
          text: 'Busque {goalShort} em vendas pela Cakto entre 1 e 30 de novembro.',
        },
        {
          title: 'Acompanhe o faturamento',
          text: 'A barra no topo do painel mostra quanto falta e atualiza junto com as suas vendas.',
        },
        {
          title: 'Conquiste o troféu',
          text: 'Apuração e entrega seguem o regulamento da campanha.',
        },
      ],
      revenueTitle: 'Faturamento em novembro',
      missingSuffix: 'para o troféu',
      achieved: 'Meta batida. O troféu é seu.',
      rulesTitle: 'O que entra na conta',
      rules: [
        'Vendas com pagamento aprovado entre 1º e 30 de novembro. A data que vale é a da aprovação do pagamento, não a da criação do pedido.',
        'Todos os meios de pagamento contam, somando todos os seus produtos.',
        'Pedido gerado e não pago não conta.',
      ],
      afterTitle: 'Depois do dia 30',
      afterText:
        'O resultado fica no topo do painel por 15 dias e depois sai. Bateu a meta: a gente entra em contato pelo e-mail do seu cadastro para combinar a entrega do troféu, conforme o regulamento. Não bateu: fica o resultado de novembro, e o seu nível no Cakto Rewards, que não zera.',
      primary: 'Ver meu relatório de vendas',
      secondary: 'Cakto Rewards',
    },

    won: {
      eyebrow: 'Troféu conquistado',
      title: 'O troféu é seu.',
      titleMuted: 'Black Cakto 100K.',
      lead: 'Você fechou {sales} em vendas aprovadas em novembro.',
      askTitle: 'Você não precisa fazer nada agora',
      askText:
        'A gente entra em contato pelo e-mail do seu cadastro para combinar a entrega, que segue o regulamento da campanha. Vale conferir se o seu e-mail está atualizado no perfil.',
      primary: 'Ver o Cakto Rewards',
      secondary: 'Minhas vendas de novembro',
      stepsTitle: 'Como chega até você',
      steps: [
        'A apuração confirma o resultado de novembro, conforme o regulamento.',
        'O contato para combinar a entrega sai pelo e-mail do seu cadastro.',
        'Depois de despachado, o rastreio chega no mesmo e-mail.',
      ],
      numbersTitle: 'Seu novembro',
      fine: 'Este aviso fica no topo do painel até 15 de dezembro e depois sai.',
    },

    missed: {
      eyebrow: 'Campanha encerrada',
      title: 'Seu novembro.',
      titleMuted: 'Black Cakto 100K.',
      lead: 'Você fez {sales} em vendas aprovadas entre 1º e 30 de novembro.',
      numbersTitle: 'O número',
      closedText:
        'O troféu Black Cakto 100K era para quem alcançasse a meta entre 1º e 30 de novembro.',
      nextTitle: 'O que continua valendo',
      nextText:
        'Seu nível no Cakto Rewards não zera com o fim da campanha: ele acompanha o seu faturamento total e continua subindo.',
      primary: 'Ver meu nível no Rewards',
      secondary: 'Relatório de novembro',
      fine: 'Este aviso fica no topo do painel até 15 de dezembro e depois sai. Novas campanhas aparecem no mesmo lugar.',
    },

    labels: {
      you: 'Você fez',
      goal: 'A meta era',
      goalRunning: 'Meta',
      difference: 'Diferença',
      approved: 'Vendas aprovadas',
      period: 'Período',
    },
  },
};
